import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import MoodSphere from './MoodSphere';
import { ArrowRightIcon, ArrowLeftIcon, SparklesIcon, BrainIcon, HeartIcon, UsersIcon, ActivityIcon } from 'lucide-react';
interface CheckInProps {
  onComplete: (data: any) => void;
  initialMood?: any;
}
const CheckIn: React.FC<CheckInProps> = ({
  onComplete,
  initialMood
}) => {
  const [step, setStep] = useState(initialMood ? 2 : 1);
  const [animateIn, setAnimateIn] = useState(false);
  const [checkInData, setCheckInData] = useState({
    mood: initialMood || null,
    // Emotional domain
    anxiety: null,
    depression: null,
    // Mental domain
    focus: null,
    thoughts: null,
    // Social domain
    connection: null,
    support: null,
    // Physical domain
    sleep: null,
    energy: null,
    // Final reflection
    reflection: ''
  });
  const totalSteps = 10;
  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(timer);
  }, []);
  const handleMoodCapture = mood => {
    setCheckInData(prev => ({
      ...prev,
      mood
    }));
    setStep(2);
  };
  const handleRatingSelect = (field, value) => {
    setCheckInData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleReflectionChange = e => {
    setCheckInData(prev => ({
      ...prev,
      reflection: e.target.value
    }));
  };
  const handleNextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onComplete(checkInData);
    }
  };
  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  // Helper function to render rating scale
  const renderRatingScale = (field, value, lowLabel, highLabel) => <div data-id="element-21">
      <div className="flex justify-between items-center py-4" data-id="element-22">
        {[1, 2, 3, 4, 5].map(ratingValue => <button key={ratingValue} className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 ${value === ratingValue ? 'bg-gray-200 border-2 border-gray-500 scale-110' : 'border-2 border-gray-300 hover:bg-gray-100'}`} onClick={() => handleRatingSelect(field, ratingValue)} data-id="element-23">
            <span className="text-base text-gray-700" data-id="element-24">{ratingValue}</span>
          </button>)}
      </div>
      <div className="flex justify-between text-sm text-gray-600 mt-2" data-id="element-25">
        <span data-id="element-26">{lowLabel}</span>
        <span data-id="element-27">{highLabel}</span>
      </div>
    </div>;
  // Render step content based on current step
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <div className="space-y-8" data-id="element-28">
            <h2 className="text-2xl font-light text-gray-800 text-center" data-id="element-29">
              Hoe voel je je op dit moment?
            </h2>
            <div className="flex justify-center py-8" data-id="element-30">
              <MoodSphere onCapture={handleMoodCapture} size="large" data-id="element-31" />
            </div>
          </div>;
      // Emotional Domain
      case 2:
        return <div className="space-y-6" data-id="element-32">
            <div className="flex items-center mb-2" data-id="element-33">
              
              <h2 className="text-xl font-light text-gray-800" data-id="element-34">
                Emotioneel welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-35">
              Hoe vaak heb je in de afgelopen week last gehad van
              angstgevoelens?
            </p>
            {renderRatingScale('anxiety', checkInData.anxiety, 'Helemaal niet', 'Vrijwel dagelijks')}
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-36">
            <div className="flex items-center mb-2" data-id="element-37">
              <BrainIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-38" />
              <h2 className="text-xl font-light text-gray-800" data-id="element-39">
                Emotioneel welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-40">
              Hoe vaak heb je je in de afgelopen week somber of hopeloos
              gevoeld?
            </p>
            {renderRatingScale('depression', checkInData.depression, 'Helemaal niet', 'Vrijwel dagelijks')}
          </div>;
      // Mental Domain
      case 4:
        return <div className="space-y-6" data-id="element-41">
            <div className="flex items-center mb-2" data-id="element-42">
              <HeartIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-43" />
              <h2 className="text-xl font-light text-gray-800" data-id="element-44">
                Mentaal welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-45">
              Hoe zou je je concentratievermogen op dit moment beoordelen?
            </p>
            {renderRatingScale('focus', checkInData.focus, 'Zeer moeilijk', 'Uitstekend')}
          </div>;
      case 5:
        return <div className="space-y-6" data-id="element-46">
            <div className="flex items-center mb-2" data-id="element-47">
              <HeartIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-48" />
              <h2 className="text-xl font-light text-gray-800" data-id="element-49">
                Mentaal welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-50">
              In welke mate heb je last van piekeren of negatieve gedachten?
            </p>
            {renderRatingScale('thoughts', checkInData.thoughts, 'Helemaal niet', 'Zeer veel')}
          </div>;
      // Social Domain
      case 6:
        return <div className="space-y-6" data-id="element-51">
            <div className="flex items-center mb-2" data-id="element-52">
              <UsersIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-53" />
              <h2 className="text-xl font-light text-gray-800" data-id="element-54">
                Sociaal welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-55">
              Hoe verbonden voel je je met de mensen om je heen?
            </p>
            {renderRatingScale('connection', checkInData.connection, 'Zeer eenzaam', 'Sterk verbonden')}
          </div>;
      case 7:
        return <div className="space-y-6" data-id="element-56">
            <div className="flex items-center mb-2" data-id="element-57">
              <UsersIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-58" />
              <h2 className="text-xl font-light text-gray-800" data-id="element-59">
                Sociaal welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-60">
              In welke mate ervaar je steun van je omgeving als je die nodig
              hebt?
            </p>
            {renderRatingScale('support', checkInData.support, 'Geen steun', 'Veel steun')}
          </div>;
      // Physical Domain
      case 8:
        return <div className="space-y-6" data-id="element-61">
            <div className="flex items-center mb-2" data-id="element-62">
              <ActivityIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-63" />
              <h2 className="text-xl font-light text-gray-800" data-id="element-64">
                Fysiek welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-65">
              Hoe heb je de afgelopen nachten geslapen?
            </p>
            {renderRatingScale('sleep', checkInData.sleep, 'Zeer slecht', 'Uitstekend')}
          </div>;
      case 9:
        return <div className="space-y-6" data-id="element-66">
            <div className="flex items-center mb-2" data-id="element-67">
              <ActivityIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-68" />
              <h2 className="text-xl font-light text-gray-800" data-id="element-69">
                Fysiek welzijn
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-70">
              Hoe zou je je energieniveau op dit moment beoordelen?
            </p>
            {renderRatingScale('energy', checkInData.energy, 'Uitgeput', 'Energiek')}
          </div>;
      // Reflection
      case 10:
        return <div className="space-y-8" data-id="element-71">
            <h2 className="text-2xl font-light text-gray-800 text-center" data-id="element-72">
              Reflectiemoment
            </h2>
            <div className="p-6 border-2 border-gray-300 rounded-xl bg-gray-50" data-id="element-73">
              <div className="flex items-start mb-4" data-id="element-74">
                <SparklesIcon className="w-5 h-5 text-gray-600 mr-3 mt-0.5" data-id="element-75" />
                <p className="text-gray-700" data-id="element-76">
                  Is er iets wat je aandacht vraagt vandaag? Of iets wat je zou
                  willen delen?
                </p>
              </div>
              <textarea className="w-full h-40 p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none" placeholder="Schrijf hier je gedachten..." value={checkInData.reflection} onChange={handleReflectionChange} data-id="element-77"></textarea>
            </div>
          </div>;
      default:
        return null;
    }
  };
  // Calculate progress percentage
  const progressPercentage = step / totalSteps * 100;
  return <div className={`w-full max-w-xl mx-auto py-8 transition-opacity duration-500 ${animateIn ? 'opacity-100' : 'opacity-0'}`} data-id="element-78">
      <div className="mb-8" data-id="element-79">
        <div className="flex justify-between items-center mb-2" data-id="element-80">
          <span className="text-sm text-gray-500" data-id="element-81">
            Stap {step} van {totalSteps}
          </span>
          <span className="text-sm text-gray-500" data-id="element-82">
            {Math.round(progressPercentage)}%
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-83">
          <div className="h-full bg-gray-700 transition-all duration-300 ease-out" style={{
          width: `${progressPercentage}%`
        }} data-id="element-84"></div>
        </div>
      </div>
      <div className="transition-opacity duration-300 min-h-[400px] flex flex-col" data-id="element-85">
        {renderStepContent()}
      </div>
      <div className="mt-12 flex justify-between" data-id="element-86">
        {step > 1 ? <Button variant="secondary" leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-88" />} onClick={handlePreviousStep} data-id="element-87">
              Vorige
            </Button> : <div data-id="element-89"></div> // Empty div to maintain flex spacing
      }
        <Button rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-91" />} onClick={handleNextStep} className="transition-transform duration-200 hover:scale-105" disabled={step === 1 && !checkInData.mood} data-id="element-90">
          {step < totalSteps ? 'Volgende' : 'Afronden'}
        </Button>
      </div>
    </div>;
};
export default CheckIn;